import { useTrackers } from '@ps/web-shared-analytics';
import { useTrackingLabel } from '@ps/web-shared-components';
import { useLayout } from '@ps/web-sports-utils';
import React, { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import useOnClickOutside from '@swf/core/hooks/useOnClickOutside';
import { Search } from '@swf/sports/components/Search/Search';

import { useSportsMenuWidget } from '../../model';
import { initialSearchResults } from '../../model/defaultState';
import { getSearchResults } from '../../utils/global-search-helpers';

import { TXTCLI_Search } from './SportsGlobalSearch.messages.json';
import styles from './SportsGlobalSearch.scss';
import { PreviousSearchResults } from './components/PreviousSearchResults/PreviousSearchResults';
import { SearchResultsContainer } from './components/SearchResults/SearchResults';

export const MIN_CHARACTERS_TO_SEARCH = 2;
export const SEARCH_QUERY_REGEX = /[^a-zA-Z0-9@\-/. ]/g;

export const SportsGlobalSearch: React.FC = () => {
  const { showNavigationSection } = useLayout();

  const { inputFilled } = useTrackers();
  const searchTrackingLabel = useTrackingLabel(TXTCLI_Search);

  const { widgetSelect, widgetDispatch } = useSportsMenuWidget();

  const searchLinks = useSelector(widgetSelect.links);
  const searchQuery = useSelector(widgetSelect.searchQuery);
  const isFetchingSearch = useSelector(widgetSelect.isFetchingSearch);

  const isQueryLengthEnough = searchQuery.length >= MIN_CHARACTERS_TO_SEARCH;
  const showPreviousSearchResults =
    useSelector(widgetSelect.showPreviousSearchResults) && !isQueryLengthEnough;

  const containerRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(containerRef, () => widgetDispatch.togglePreviousSearchResults(false));

  const getAndSetSearchResults = useCallback(() => {
    const searchResults = getSearchResults(searchQuery.toLowerCase(), searchLinks);
    widgetDispatch.setSearchResults({ results: searchResults, isNewSearchQuery: false });
  }, [searchLinks, searchQuery, widgetDispatch]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Trim only at the start as some events might be matching by space at the end
    const inputValue = event.target.value.trimStart();
    const formattedInputValue = inputValue.replace(SEARCH_QUERY_REGEX, '');
    widgetDispatch.updateSearchQuery(formattedInputValue);
  };

  const handleSearchClear = () => {
    widgetDispatch.updateSearchQuery('');
  };

  const handleSearchFocus = () => {
    if (!searchQuery) {
      widgetDispatch.searchRequested();
    }
  };

  const handleSearchBlur = (event: React.FocusEvent<HTMLInputElement, Element>) => {
    inputFilled({
      fieldType: 'text',
      fieldName: searchTrackingLabel,
      strInput: event.target.value
    });
  };

  useEffect(() => {
    const isMenuClosed = !showNavigationSection;

    if (isMenuClosed) {
      widgetDispatch.updateSearchQuery('');
    }
  }, [showNavigationSection, widgetDispatch]);

  useEffect(() => {
    if (isFetchingSearch) {
      return;
    }

    if (!isQueryLengthEnough) {
      widgetDispatch.setSelectedEventType(undefined);
      widgetDispatch.setSearchResults({ results: initialSearchResults, isNewSearchQuery: true });
      return;
    }

    getAndSetSearchResults();
  }, [widgetDispatch, isFetchingSearch, getAndSetSearchResults, isQueryLengthEnough, searchQuery]);

  return (
    <div
      ref={containerRef}
      className={styles.container}
      role="search"
      onFocus={() => widgetDispatch.togglePreviousSearchResults(true)}
    >
      <Search
        searchTerm={searchQuery}
        onChange={handleSearchChange}
        onClear={handleSearchClear}
        placeholderTranslation={TXTCLI_Search}
        trackingLabel={searchTrackingLabel}
        onFocus={handleSearchFocus}
        onBlur={handleSearchBlur}
      />
      {isQueryLengthEnough && <SearchResultsContainer />}
      {showPreviousSearchResults && <PreviousSearchResults />}
    </div>
  );
};
